import { Publish } from '@mui/icons-material';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { modalManagerStore } from '@/features/ModalManagerStore';
import { colorStore } from '@/features/queueColors';
import { DraftTimelog } from '@/types';
import { formatDurationRussian } from '@/utils/formatDurationRussian';

import { PublishTargetMenu } from '../PublishTargetMenu';
import { useDraftTimelogStore } from '../../model';

type DraftLogItemProps = {
  draft: DraftTimelog;
};

export const DraftLogItem = observer(({ draft }: DraftLogItemProps) => {
  const draftTimelogStore = useDraftTimelogStore();

  const keyPrefix = draft.taskKey?.split('-')[0] ?? draft.queueKey ?? null;
  const color = keyPrefix ? colorStore.getColorByPrefix(keyPrefix) : undefined;
  const needsBorder = keyPrefix ? colorStore.needsBorder(keyPrefix) : false;

  return (
    <Box
      sx={{
        cursor: 'pointer',
        borderRadius: 1,
        bgcolor: 'background.paper',
        px: 1.25,
        py: 1.5,
        border: '1px dashed',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: color ?? 'text.disabled',
        opacity: color ? 1 : 0.9,
        transition: 'background-color 0.2s',
        '&:hover': { bgcolor: 'action.hover' },
        ...(color &&
          needsBorder && {
            boxShadow: 'inset 3px 0 0 var(--shadow-color-strong)',
          }),
      }}
      onClick={() => modalManagerStore.open('editDraft', draft)}
    >
      <Box
        sx={{ display: 'flex', minWidth: 0, alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {draft.taskKey ? (
              <Typography
                component="span"
                variant="body2"
                fontWeight={600}
                sx={{
                  display: 'inline-block',
                  maxWidth: '100%',
                  wordBreak: 'break-all',
                  overflowWrap: 'anywhere',
                  color,
                  ...(needsBorder && {
                    textShadow:
                      '0 0 1px var(--shadow-color-strong), 0 0 1px var(--shadow-color-strong)',
                  }),
                }}
              >
                {draft.taskKey}
              </Typography>
            ) : draft.queueKey ? (
              <Typography component="span" variant="body2" fontWeight={600} color="text.secondary">
                {draft.queueKey}
              </Typography>
            ) : (
              <Typography
                component="span"
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                fontStyle="italic"
              >
                Без задачи
              </Typography>
            )}
            <Chip
              label="Черновик"
              variant="outlined"
              size="small"
              sx={{ fontSize: 10, height: 20 }}
            />
          </Box>
          {(draft.taskTitle || draft.queueName) && (
            <Typography
              variant="caption"
              fontWeight={500}
              noWrap
              sx={{ display: 'block', py: 0.25 }}
            >
              {draft.taskTitle ?? draft.queueName}
            </Typography>
          )}
          {draft.comment && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: 11,
              }}
            >
              {draft.comment}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={formatDurationRussian(draft.durationMinutes)}
            size="small"
            sx={{ fontSize: 12 }}
          />
          <PublishTargetMenu
            onPublish={(target) => draftTimelogStore.publishDraft(draft.id, target)}
          >
            {(open) => (
              <Tooltip title={!draft.taskKey ? 'Укажите задачу для публикации' : 'Опубликовать'}>
                <span>
                  <IconButton size="small" disabled={!draft.taskKey} onClick={open}>
                    <Publish sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </PublishTargetMenu>
        </Box>
      </Box>
    </Box>
  );
});
